import type { Relation } from '../model';
import { LabelPlacementHelper, type LabelText } from './label-placement-helper';
import {
  LayoutGeometryHelper as G,
  type NodeFrame,
} from './layout-geometry-helper';
import type { Lane, SharedPlacement } from './multi-owner-helper';
import type {
  DraftLine,
  PlacedNode,
  ShareLabel,
} from './structure-layout.types';

export interface BandRowItem {
  entityId: number;
  x: number;
  width: number;
}

export interface BandResult {
  childX: number;
  childTop: number;
  lines: DraftLine[];
  labels: ShareLabel[];
}

interface ChannelledLane {
  lane: Lane;
  owner: PlacedNode;
  channelX: number;
}

interface RoutedLane extends ChannelledLane {
  entryX: number;
}

class SharedBandHelper {
  static readonly LANE_SPACING = 16;

  static readonly BAND_GAP = 40;

  static readonly LEVEL_GAP = 12;

  static readonly ENTRY_GAP = 40;

  static getGutters(
    shared: ReadonlyArray<SharedPlacement>,
    labelOf: (relation: Readonly<Relation>) => LabelText | null
  ): Map<number, number> {
    const gutters = new Map<number, number>();
    SharedBandHelper.groupByBlock(
      shared.flatMap((placement) => placement.lanes)
    ).forEach((lanes, blockId) => {
      gutters.set(
        blockId,
        SharedBandHelper.getLabelSpace(lanes, labelOf) +
          lanes.length * SharedBandHelper.LANE_SPACING
      );
    });
    return gutters;
  }

  // Lanes nearest the child cross the band on the highest level, so no two lanes cross.
  static place(
    placement: Readonly<SharedPlacement>,
    row: ReadonlyArray<BandRowItem>,
    nodeOf: (entityId: number) => PlacedNode | undefined,
    child: Readonly<{ anchorX: number }>,
    rowBottom: number,
    labelOf: (relation: Readonly<Relation>) => LabelText | null,
    frame: Readonly<NodeFrame> = G.CARD_FRAME
  ): BandResult {
    const channels = SharedBandHelper.getChannels(
      placement,
      row,
      nodeOf,
      labelOf
    );
    const blocks = row.filter((item) =>
      placement.lanes.some((lane) => lane.blockId === item.entityId)
    );
    const left = Math.min(...blocks.map((item) => item.x));
    const right = Math.max(...channels.map((lane) => lane.channelX));
    const centre = Math.round((left + right) / 2);
    const entries = SharedBandHelper.getEntries(
      channels.map((lane) => lane.channelX),
      centre - frame.width / 2,
      frame.width,
      frame.topEntryInsetOf(placement.entityId)
    );
    const routed = channels.map((lane, i) => ({ ...lane, entryX: entries[i] }));

    const levelOf = SharedBandHelper.getLevels(routed);
    const levelCount =
      Math.max(0, ...levelOf.values()) + (levelOf.size > 0 ? 1 : 0);
    const bandTop = rowBottom + SharedBandHelper.BAND_GAP;
    const childTop =
      bandTop +
      Math.max(0, levelCount - 1) * SharedBandHelper.LEVEL_GAP +
      SharedBandHelper.ENTRY_GAP;
    const lines = routed.map(({ lane, owner, channelX, entryX }): DraftLine => {
      const startY = owner.y + owner.height / 2;
      const levelY =
        bandTop +
        (levelOf.get(lane.relation.id) ?? 0) * SharedBandHelper.LEVEL_GAP;
      return {
        relationId: lane.relation.id,
        kind: lane.relation.kind,
        points: G.simplify([
          {
            x: owner.x + owner.width - frame.sideInsetOf(owner.entityId),
            y: startY,
          },
          { x: channelX, y: startY },
          { x: channelX, y: levelY },
          { x: entryX, y: levelY },
          { x: entryX, y: childTop },
        ]),
      };
    });
    const labels = routed.flatMap(({ lane, owner }) => {
      const label = labelOf(lane.relation);
      return label
        ? [LabelPlacementHelper.atOwner(lane.relation.id, label, owner)]
        : [];
    });
    return { childX: centre - child.anchorX, childTop, lines, labels };
  }

  static getEntries(
    channels: ReadonlyArray<number>,
    childLeft: number,
    childWidth = G.NODE_WIDTH,
    inset = G.ENTRY_INSET
  ): number[] {
    const lo = childLeft + inset;
    const hi = childLeft + childWidth - inset;
    const spacing =
      channels.length > 1
        ? Math.min(
            SharedBandHelper.LANE_SPACING,
            (hi - lo) / (channels.length - 1)
          )
        : 0;
    const entries: number[] = [];
    channels.forEach((channelX, i) => {
      entries.push(
        Math.max(
          Math.min(Math.max(channelX, lo), hi),
          i > 0 ? entries[i - 1] + spacing : lo
        )
      );
    });
    for (let i = entries.length - 1; i >= 0; i -= 1) {
      entries[i] = Math.min(
        entries[i],
        i < entries.length - 1 ? entries[i + 1] - spacing : hi
      );
    }
    return entries.map((x) => Math.round(x));
  }

  private static getChannels(
    placement: Readonly<SharedPlacement>,
    row: ReadonlyArray<BandRowItem>,
    nodeOf: (entityId: number) => PlacedNode | undefined,
    labelOf: (relation: Readonly<Relation>) => LabelText | null
  ): ChannelledLane[] {
    const routed: ChannelledLane[] = [];
    SharedBandHelper.groupByBlock(placement.lanes).forEach((lanes, blockId) => {
      const item = row.find((candidate) => candidate.entityId === blockId);
      if (!item) throw new Error(`Owner block ${blockId} is not in the row`);
      const inner =
        item.x + item.width + SharedBandHelper.getLabelSpace(lanes, labelOf);
      lanes
        .map((lane) => {
          const owner = nodeOf(lane.relation.ownerId);
          if (!owner)
            throw new Error(`Owner ${lane.relation.ownerId} is not placed`);
          return { lane, owner };
        })
        .sort((a, b) => b.owner.y - a.owner.y)
        .forEach(({ lane, owner }, i) => {
          routed.push({
            lane,
            owner,
            channelX: inner + (i + 1) * SharedBandHelper.LANE_SPACING,
          });
        });
    });
    return routed.sort((a, b) => a.channelX - b.channelX);
  }

  private static getLevels(
    routed: ReadonlyArray<RoutedLane>
  ): Map<string, number> {
    const levels = new Map<string, number>();
    const rightward = routed
      .filter((lane) => lane.entryX > lane.channelX)
      .sort((a, b) => b.channelX - a.channelX);
    const leftward = routed
      .filter((lane) => lane.entryX < lane.channelX)
      .sort((a, b) => a.channelX - b.channelX);
    [rightward, leftward].forEach((lanes) =>
      lanes.forEach((lane, i) => levels.set(lane.lane.relation.id, i))
    );
    return levels;
  }

  private static getLabelSpace(
    lanes: ReadonlyArray<Lane>,
    labelOf: (relation: Readonly<Relation>) => LabelText | null
  ): number {
    const widest = Math.max(
      0,
      ...lanes.map((lane) => labelOf(lane.relation)?.width ?? 0)
    );
    return widest > 0 ? G.LABEL_GAP + widest : 0;
  }

  private static groupByBlock(lanes: ReadonlyArray<Lane>): Map<number, Lane[]> {
    const byBlock = new Map<number, Lane[]>();
    lanes.forEach((lane) =>
      byBlock.set(lane.blockId, [...(byBlock.get(lane.blockId) ?? []), lane])
    );
    return byBlock;
  }
}

export { SharedBandHelper };
